import { ICertificateFullInfo } from '@repo/types';
import { useState } from 'react';
import { Stack, Typography } from '@mui/material';
import { Alert, Button } from '@repo/ui';
import { DrawerPopup } from 'components/UI/DrawerPopup';
import { resolveUserIdentity } from 'components/UI/UserPreview';
import { useClaimOwnership } from 'hooks/api/transfers/useClaimOwnership';
import { useCancelTransfer } from 'hooks/api/transfers/useCancelTransfer';
import { GenericSuccessDrawer } from 'components/genericSuccessDrawer';

/**
 * VRG-161 — this section used to read the transfer's `code` off the public
 * certificate response (`certificate.transfer.code`) and submit it after a
 * cosmetic scan step. That code is a 4-digit value returned to UNAUTHENTICATED
 * callers (the actual leak this fixes) and is being stripped from every
 * certificate/transfer serializer in this same change. Claiming now goes
 * through the VRG-138 email-bound claim token instead (`claim_token` query
 * param, carried onto this route from the gift-init email link by
 * `App.tsx` — see the VRG-138 comment there for why it's NOT named `token`,
 * which `ScanTag` already uses for an unrelated mobile-scan-session flow).
 */
export const GiftInActionsSection = ({
  certificate,
  shouldShowOnCertificatePage,
}: {
  certificate: ICertificateFullInfo;
  shouldShowOnCertificatePage: boolean;
}) => {
  const [isSuccessDrawerOpen, setIsSuccessDrawerOpen] = useState(false);
  const [isDenyConfirmOpen, setIsDenyConfirmOpen] = useState(false);

  // VERA-304: name the SENDER in the confirmation, so "goes back to …" is concrete
  // rather than abstract. Reuses `resolveUserIdentity` — the same name→@handle→email
  // fallback the gift card and the recipient picker use — so the person is described
  // identically everywhere in this flow. Falls back to "the sender" when the payload
  // carries no identifiable party, which is better than rendering an empty gap.
  const sender = (certificate.transfer as { fromCustomer?: unknown } | undefined)?.fromCustomer;
  const resolvedSender = sender ? resolveUserIdentity(sender as never).name : null;
  // `resolveUserIdentity` returns an em-dash placeholder when it can identify nobody;
  // that reads as a rendering bug mid-sentence, so treat it as "no name".
  const senderName = resolvedSender && resolvedSender !== '—' ? resolvedSender : 'the sender';
  const [successCopy, setSuccessCopy] = useState({
    title: 'Ownership claimed',
    description: `You are now the official owner of the ${certificate.product?.title}.`,
  });
  const [error, setError] = useState<string | null>(null);

  // VERA-304: the shared claim path, identical to TransferInActionsSection's.
  // These two sections are the same decision on two surfaces and they HAD
  // drifted — see `useClaimOwnership` for the fold that caused it.
  const { claim, isPending: isClaiming } = useClaimOwnership(certificate.id);
  // VRG-161 (step 4) — "Deny gift" needs no credential of its own: the
  // recipient (`to_owner_id`) is allowed to cancel a still-pending
  // customer-initiated transfer purely on their authenticated identity (see
  // CancelCustomerInitiatedTransferUseCase — accepts either party while the
  // certificate is TransferInitiated/TransferWaitingConfirmation, which is
  // exactly this section's visible window). Rejected the two other cancel/
  // reject use-cases: reject-incoming-transfer-cancel-request-by-buyer
  // handles a DIFFERENT case (seller already requested a cancel AFTER
  // accept, and the buyer is refusing that cancel request — the opposite of
  // "decline the gift").
  const { mutateAsync: cancelTransfer, isPending: isDenying } = useCancelTransfer({
    certificateId: certificate?.id ?? '',
  });

  // VERA-304: deliberately NOT an early `return null`. Claiming or denying settles the
  // transfer, `TransferSummarySection` then drops `shouldShowOnCertificatePage`, and an
  // early return would unmount this component — taking the success drawer rendered
  // inside it down with it, mid-read. That is the same teardown `useInitTransfer`
  // documents.
  //
  // So gate only the PENDING-state controls and always render the drawer, which is the
  // shape `GiftOutActionsSection` already had (and why cancel's drawer survived while
  // claim's and deny's did not). The page updating underneath is correct and wanted —
  // dismissing the drawer reveals the owner view.

  const handleClaim = async () => {
    setError(null);
    const result = await claim();
    if (result.ok) {
      setSuccessCopy({
        title: 'Ownership claimed',
        description: `You are now the official owner of the ${certificate.product?.title}.`,
      });
      setIsSuccessDrawerOpen(true);
      return;
    }
    setError(result.message);
  };

  /**
   * VERA-304: declining is CONFIRMED first — it does not fire on the button press.
   *
   * Claiming passes through a scan (or an emailed token); declining passed through
   * nothing, so the DESTRUCTIVE path was the frictionless one. VRG-161 noted that
   * same asymmetry from the other side when it argued the claim token should not be
   * required to accept. Refusing a gift sends the item back to the sender and cannot
   * be undone from here — the sender has to notice and send it again — so it earns a
   * gate, and the gate must NAME that consequence rather than ask "are you sure?"
   * (§43: a confirmation's job is to state the effect).
   */
  const handleDenyConfirmed = async () => {
    setError(null);
    setIsDenyConfirmOpen(false);
    try {
      await cancelTransfer(certificate.id ?? '');
      setSuccessCopy({
        title: 'Gift declined',
        description: 'You have declined this gift.',
      });
      setIsSuccessDrawerOpen(true);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to decline the gift. Please try again.');
    }
  };

  return (
    <>
      {/* VERA-315 moved the primary's PIXELS into the page's sticky bottom bar via
          ActionBarPortal; VERA-304 replaced what the primary DOES. Both survive: the
          button renders in the bar, and it runs the VRG-138 token claim rather than
          315's `useAcceptTransfer` + ScanTag scan step, which this epic superseded.
          (315's version still opened a scan drawer — that path is gone, along with the
          leaked transfer `code` it submitted.)

          VERA-304: the primary action is ALWAYS a "Claim Gift" button, and it
          ALWAYS works. This went through three states, and the middle one is the
          instructive failure:

          The message still exists, but on demand: without a token, pressing Claim
          explains what's needed via the same inline `error` line the claim failure
          path already uses. Note this state is mostly an artifact of opening the page
          directly — a real recipient arrives FROM the emailed link, which carries the
          token, so the button claims immediately.

          NOT changed here: whether an authenticated recipient should be allowed to
          claim with no token at all. `POST /transfer/customer/claim` is already
          `@RequireOwner()` + auth-guarded ("must belong to a transfer inviting the
          authenticated caller"), so the token is a SECOND factor on top of identity,
          not the only one. Dropping it is defensible but it is a security-model
          decision, so it is surfaced rather than taken. */}
      {/*
        The action renders HERE, inside the state card, not in a pinned bottom bar.

        It used to portal into the page's StickyActionBar, which put a bare verb a
        full screen away from the block explaining it: "Cancel gift" alone does not
        say which gift, to whom, or that it is still unclaimed — and this card says
        all three. The explanation and the action that resolves it belong together;
        separating them was the defect, and relocating the button was the fix.

        Only the pixels moved. The mutation, the scan-tag drawer and the success
        drawer it drives all still live in this component.
      */}
      {/* FULL WIDTH at the bottom of the card — the shape every state card
          shares, so gift-out, gift-in and transfer-in read as one family rather
          than three arrangements. It stays SECONDARY: this is the card's action,
          not the page's primary CTA, and it is usually a reversal (cancel, deny),
          which a solid fill oversells. */}
        <Button
          className='w-full'
          sx={{ paddingX: 2 }}
          variant='primary'
          /* VERA-304: ALWAYS `handleClaim` — never gated on `claimToken`.
             The merge with VERA-315 reintroduced `claimToken ? handleClaim :
             setError(NO_TOKEN_MESSAGE)`, which is the dead end this epic
             superseded: the in-app recipient has no emailed token, so the
             button existed but could only ever produce a sentence telling them
             to go find their email. `handleClaim` sends `{certificateId}` when
             there is no token and the endpoint authorises on identity either
             way.

             That fold hit BOTH incoming sections and only this one was
             restored — TransferInActionsSection carried the dead end until a
             recipient hit it live. The claim now lives in `useClaimOwnership`,
             shared by both, so a third revert cannot fix one half and miss the
             other. */
          onClick={handleClaim}
          disabled={isClaiming || isDenying}
        >
          {isClaiming ? 'Claiming…' : 'Claim Gift'}
        </Button>
      {/* Deny stays INLINE. 315 kept it inline because it was a dead button and pinning
          a dead control to every screen is worse than leaving it in the flow; that
          reasoning is now spent (VRG-161 step 4 wired it to cancelTransfer), but the
          placement still stands on its own — the bar holds ONE primary, and declining a
          gift is a secondary action that belongs beside the thing it declines. */}
      {shouldShowOnCertificatePage && (
        <Button
          className='w-full mt-3'
          variant='secondary'
          sx={{ paddingX: 2 }}
          onClick={() => setIsDenyConfirmOpen(true)}
          disabled={isClaiming || isDenying}
        >
          {isDenying ? 'Declining…' : 'Deny gift'}
        </Button>
      )}
      {/* VERA-304: an error is an Alert, not red body text. Bare `color='error'`
          prose reads as if the PAGE is broken — it sits in the card's normal text
          flow with nothing marking it as a status surface, so a piece of guidance
          ("open your gift email") looked like a failure the user had caused.
          The Alert gives it the container, icon and severity that say "this is a
          message about your last action", which is what it actually is. */}
      {error && (
        <Alert severity='error' description={error} sx={{ mt: 2 }} />
      )}
      {/* VERA-304: the confirmation gate for declining. Reuses the house `DrawerPopup`
          rather than a hand-rolled sheet (§28/§47), so it carries the same bottom-sheet
          chrome as every other drawer in this flow.

          The title is the ACTION ("Decline Gift"), not the outcome — the outcome
          ("Gift declined") belongs to the success drawer that follows. Naming a
          not-yet-taken action in the past tense tells the user it already happened,
          which is precisely what the confirm step exists to prevent. */}
      <DrawerPopup
        title='Decline Gift'
        handleOpen={isDenyConfirmOpen}
        handleClose={() => setIsDenyConfirmOpen(false)}
        child={
          <Stack sx={{ pt: 1, pb: 2 }}>
            {/* §43: name the actual effect in the user's terms. "Are you sure?" would
                gate the action without ever saying what it costs. §15: separate
                sentences, generous line-height — not em-dash-joined clauses. */}
            <Typography variant='body2' sx={{ color: 'text.secondary', lineHeight: 1.5, mb: 3 }}>
              This gift goes back to {senderName}. It will not be added to your
              certificates, and you cannot undo this yourself. They would need to send it
              again.
            </Typography>
            <Button
              className='w-full'
              variant='primary'
              sx={{ paddingX: 2 }}
              onClick={handleDenyConfirmed}
              disabled={isDenying}
            >
              {isDenying ? 'Declining…' : 'Decline gift'}
            </Button>
            <Button
              className='w-full mt-3'
              variant='secondary'
              sx={{ paddingX: 2 }}
              onClick={() => setIsDenyConfirmOpen(false)}
              disabled={isDenying}
            >
              Keep it
            </Button>
          </Stack>
        }
      />
      <GenericSuccessDrawer
        open={isSuccessDrawerOpen}
        onClose={() => setIsSuccessDrawerOpen(false)}
        certificate={certificate}
        drawerTitle={successCopy.title}
        bodyTitle={successCopy.title}
        description={successCopy.description}
        buttonLabel='View certificate'
      />
    </>
  );
};
